/**
 * Two JSON payloads as one table: `key | before | after`, indented, foldable.
 *
 * The sibling of `JsonKeyValueTable`, which shows ONE payload. They share the
 * collapse interaction (`json-tree.tsx`) and every presentation primitive, and
 * differ in exactly one behaviour: this one knows which subtrees changed and
 * opens those, while a lone payload knows nothing and opens everything.
 *
 * Why a hierarchy rather than a flat list of dotted paths: a payload
 * replacement produces hundreds of leaves, and twenty rows all beginning
 * `variants[0].` answer "what changed" no better than the blob they replaced.
 * Folding is what makes the answer short.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@resource-kit/ui';
import {
  TREE_NODE_ROW_CLASS,
  TreeKeyCell,
  useCollapsibleTree,
  type CollapsibleRow,
  containerSummary,
  formatScalar,
} from '@resource-kit/ui/react';
import { structuralJsonDiff, type DiffNode } from '@lib/json-diff';

export interface JsonDiffTableProps {
  before: unknown;
  after: unknown;
  /**
   * Show only the paths leading to a change. Controlled: the switch lives in
   * `JsonDiffPanel`, which renders this table twice (inline and in the
   * fullscreen dialog) and needs both to agree.
   */
  showChangedOnly?: boolean;
  /** Chrome rendered above the table, inside its border. See `JsonKeyValueTable`. */
  toolbar?: ReactNode;
  ariaLabel?: string;
  className?: string;
}

/**
 * How many siblings render under one parent before the rest fold into a count.
 *
 * The flat rendering this replaces capped leaf rows at 20 for a load-bearing
 * reason: a payload REPLACEMENT — a column that gained ten new sub-objects at
 * once — produces hundreds of leaves. Opening every changed container would
 * have silently deleted that protection, since after a replacement every
 * container qualifies as changed.
 */
const SIBLING_BUDGET = 20;

type DiffRow = CollapsibleRow &
  ({ kind: 'node'; node: DiffNode } | { kind: 'more'; parentPath: string; remaining: number });

/**
 * A subtree with no change in it starts folded; one with a change starts open.
 *
 * Module-level so its identity is stable and the memoised visible-row list
 * survives unrelated re-renders.
 */
const collapseUnchanged = (row: DiffRow) => row.kind === 'node' && row.node.changedCount === 0;

/** Flatten the tree into DFS rows, applying the filter and the sibling budget. */
function flatten(
  node: DiffNode,
  depth: number,
  rows: DiffRow[],
  showChangedOnly: boolean,
  budgetLifted: ReadonlySet<string>,
): void {
  const kids = showChangedOnly
    ? node.children.filter((child) => child.changedCount > 0)
    : node.children;
  const shown =
    budgetLifted.has(node.path) || kids.length <= SIBLING_BUDGET
      ? kids
      : kids.slice(0, SIBLING_BUDGET);

  for (const child of shown) {
    rows.push({ kind: 'node', path: child.path, depth, node: child });
    if (child.children.length > 0) flatten(child, depth + 1, rows, showChangedOnly, budgetLifted);
  }

  if (shown.length < kids.length) {
    rows.push({
      kind: 'more',
      // This is the row's React key, sharing one key space with the node rows
      // keyed by their JSON path. Without a separator the root case would be
      // plain `more`, colliding outright with a root key of that name. U+0000
      // is not airtight — JSON does permit it in a key — but nothing that
      // reaches this table emits one.
      path: `${node.path}\x00more`,
      depth,
      parentPath: node.path,
      remaining: kids.length - shown.length,
    });
  }
}

/**
 * What a container row says about itself, beside the key it qualifies.
 *
 * Beside the key, and not in the value columns, because a word placed under
 * "Before" reads as a before VALUE: a container that had just appeared showed
 * "Added" exactly where its old value would go. Beside the key there is room
 * for a mark rather than a word — and the mark carries the count, which the
 * word never did. `+11` says both what happened and how much of it.
 *
 * `null` for an unchanged container: the fold arrow and the size are already
 * saying "nothing moved here", and a fifth badge competing with them is noise.
 */
function ContainerMark({ node }: { node: DiffNode }) {
  const { t } = useTranslation();
  if (!node.selfChanged && node.changedCount === 0) return null;

  // A container cleared to `null` still exists on the after side, so a signed
  // count would claim its fields went somewhere. They were emptied.
  if (node.selfChanged && node.after === null) {
    return (
      <span className="ml-1.5 font-medium text-destructive" title={t('json.diff.cleared')}>
        ∅
      </span>
    );
  }
  const gone = node.selfChanged && node.after === undefined;
  const arrived = node.selfChanged && !gone;
  const sign = gone ? '−' : arrived ? '+' : '~';
  const tone = gone ? 'text-destructive' : arrived ? 'text-foreground' : 'text-muted-foreground';
  const title = gone
    ? t('json.diff.removed')
    : arrived
      ? t('json.diff.added')
      : t('json.diff.changed', { count: node.changedCount });

  return (
    <span className={cn('ml-1.5 font-medium tabular-nums', tone)} title={title}>
      {sign}
      {node.changedCount}
    </span>
  );
}

export function JsonDiffTable({
  before,
  after,
  showChangedOnly = true,
  toolbar,
  ariaLabel,
  className,
}: JsonDiffTableProps) {
  const { t } = useTranslation();
  const [budgetLifted, setBudgetLifted] = useState<ReadonlySet<string>>(() => new Set());

  const tree = useMemo(() => structuralJsonDiff(before, after), [before, after]);

  const rows = useMemo<DiffRow[]>(() => {
    if (tree === null) return [];
    const out: DiffRow[] = [];
    flatten(tree.root, 0, out, showChangedOnly, budgetLifted);
    return out;
  }, [tree, showChangedOnly, budgetLifted]);

  const collapse = useCollapsibleTree(rows, collapseUnchanged);

  if (tree === null) return null;

  // Both sides parsed, and nothing inside them differs. Reachable because the
  // backend compares the SERIALIZED column: the same payload re-serialized with
  // a different key order is recorded as a change with nothing different.
  if (tree.changedCount === 0 && showChangedOnly) {
    return (
      <div className={cn('overflow-hidden rounded-md border border-border bg-muted/30', className)}>
        {toolbar}
        <div className="px-2 py-2">
          <span className="text-xs text-muted-foreground italic">
            {t('audit.jsonDiff.equivalent')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        '@container overflow-hidden rounded-md border border-border bg-muted/30',
        className,
      )}
    >
      {toolbar}
      <table
        className="w-full border-collapse text-xs"
        aria-label={ariaLabel ?? t('json.diff.tableAria')}
      >
        <thead>
          {/* The three columns are always in the DOM. Below the container's
              breakpoint they are re-laid-out as a grid so before and after
              stack instead of squeezing the key column to nothing — a layout
              change, so jsdom cannot see it and only a browser can verify it. */}
          <tr className="@max-[34rem]:grid @max-[34rem]:grid-cols-[40%_60%]">
            <th className="w-1/3 min-w-[160px] border-b border-r border-border/60 py-1.5 pl-2 pr-3 text-left font-medium text-muted-foreground @max-[34rem]:row-span-2">
              {t('json.diff.colKey')}
            </th>
            <th className="border-b border-border/60 py-1.5 pl-3 pr-2 text-left font-medium text-muted-foreground">
              {t('json.diff.colBefore')}
            </th>
            <th className="border-b border-border/60 py-1.5 pl-3 pr-2 text-left font-medium text-muted-foreground">
              {t('json.diff.colAfter')}
            </th>
          </tr>
        </thead>
        <tbody>
          {collapse.visible.map((row, index) => {
            const notLast = index !== collapse.visible.length - 1;

            if (row.kind === 'more') {
              return (
                <tr key={row.path} data-more={row.parentPath}>
                  <td
                    colSpan={3}
                    className={cn('py-1 pr-2', notLast && 'border-b border-border/60')}
                    style={{ paddingLeft: `${8 + row.depth * 16}px` }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setBudgetLifted((current) => new Set(current).add(row.parentPath))
                      }
                      className="rounded-sm text-[11px] text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      {t('json.diff.moreRows', { count: row.remaining })}
                    </button>
                  </td>
                </tr>
              );
            }

            const { node } = row;
            const hasChildren = collapse.hasChildren(row);
            const isLeaf = node.kind === 'leaf';

            return (
              <tr
                key={row.path}
                data-path={row.path}
                className={cn(
                  '@max-[34rem]:grid @max-[34rem]:grid-cols-[40%_60%]',
                  notLast && 'border-b border-border/60',
                  hasChildren && TREE_NODE_ROW_CLASS,
                )}
                onClick={hasChildren ? () => collapse.toggle(row) : undefined}
              >
                <TreeKeyCell
                  depth={row.depth}
                  expanded={!collapse.isCollapsed(row)}
                  hasChildren={hasChildren}
                  label={node.key}
                  // `align-top` is a table-cell property and stops applying once the row
                  // becomes a grid, which left the key floating in the middle of a
                  // two-line stacked row.
                  // `w-full` because `w-1/3` floors at `min-w-[160px]`, which leaves the
                  // key column narrower than its own grid track — long keys then wrap
                  // after the twisty spacer and start 16px left of their siblings.
                  className="text-left @max-[34rem]:w-full @max-[34rem]:row-span-2 @max-[34rem]:self-start"
                >
                  <span className={cn(!isLeaf && 'text-foreground font-medium')}>{node.key}</span>
                  {!isLeaf && (
                    <>
                      {/* The side that EXISTS. Reading `before` unconditionally
                          is right by accident for a removal and wrong for an
                          addition, where it printed `{}` over eleven new keys. */}
                      <span className="ml-1.5">{containerSummary(node.before ?? node.after)}</span>
                      <ContainerMark node={node} />
                    </>
                  )}
                </TreeKeyCell>

                {isLeaf ? (
                  <>
                    <td
                      className={cn(
                        'align-top break-all py-1.5 pl-3 pr-2 font-mono',
                        node.selfChanged && 'bg-destructive/10 text-foreground',
                        // A dash already means "there was nothing here"; striking
                        // it through would say the nothing was deleted.
                        node.selfChanged && node.before !== undefined && 'line-through',
                      )}
                    >
                      {formatScalar(node.before)}
                    </td>
                    <td
                      className={cn(
                        'align-top break-all py-1.5 pl-3 pr-2 font-mono',
                        node.selfChanged && 'bg-success/10 text-foreground',
                      )}
                    >
                      {formatScalar(node.after)}
                    </td>
                  </>
                ) : (
                  // Empty on purpose: the container's story is told beside its
                  // key, so nothing here can be mistaken for an old or new value.
                  <>
                    <td className="py-1.5 pl-3 pr-2" />
                    <td className="py-1.5 pl-3 pr-2" />
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default JsonDiffTable;
